import { FunctionComponent, useCallback } from 'react'
import styled, { css } from 'styled-components'

import { VideoRecommendedItem } from '@hooks/useRecommendations'
import Blink from '@components/Blink'
import VideoLabel from '@components/VideoLabel'

import Title from './Title'
import ImageComponent from './Image'
import { useQueryClient } from '@tanstack/react-query'
import useViewportTracking from '@hooks/useViewportTracking'
import { triggerAureusImpression } from '@utils/aureus'

export type VideoRecommendationsItemProps = VideoRecommendedItem & {
  isFullScreen?: boolean
  onClick?: (item: VideoRecommendedItem) => void
}

const StyledVideoRecommendationsItem = styled(Blink)`
  display: block;
  appearance: none;
  text-decoration: none;
`

const GridWrapper = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  grid-template-columns: minmax(0, 1fr);
  align-items: flex-end;
`

const TextStack = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: flex-end;
  justify-items: flex-start;
`

const GradientStack = styled.div`
  ${({
    theme: {
      spacing: { spacing8 },
      colors: { staticBlack },
      utils: { opacity },
    },
  }) => css`
    box-sizing: border-box;
    position: relative;
    width: 100%;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    align-items: flex-start;
    justify-items: flex-start;
    padding: 0 ${spacing8} ${spacing8} ${spacing8};

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      background: linear-gradient(
        4deg,
        ${opacity(staticBlack, '99')} 50%,
        ${opacity(staticBlack, '00')} 90%
      );
    }
  `}
`

const StyledVideoLabel = styled(VideoLabel)`
  ${({
    theme: {
      spacing: { spacing12 },
    },
  }) => css`
    margin-bottom: ${spacing12};
  `}
`

const VideoRecommendationsItem: FunctionComponent<
  VideoRecommendationsItemProps
> = ({ onClick, isFullScreen, ...item }) => {
  const { title, link, image, targetContentType, video, aureusOfferId } = item

  const queryClient = useQueryClient()

  const onClickHandler = useCallback(() => {
    if (onClick) {
      onClick(item)
    }
  }, [onClick, item])

  const onImpression = useCallback(() => {
    triggerAureusImpression({
      aureusOfferId,
      queryClient,
    })
  }, [aureusOfferId, queryClient])

  const viewportRef = useViewportTracking({
    track: true,
    onImpression,
  })

  const clickableProps = {
    ...link,
    aureusOfferId,
    onClick,
    ...(link?.title ? { ariaLabel: link.title } : {}),
  }

  return (
    <StyledVideoRecommendationsItem
      {...clickableProps}
      ref={viewportRef}
      onClick={onClickHandler}>
      <ImageComponent {...image}>
        <GridWrapper>
          <TextStack>
            <GradientStack>
              {!!video && (
                <StyledVideoLabel
                  duration={video.duration}
                  targetContentType={targetContentType}
                />
              )}
              <Title isFullScreen={isFullScreen}>{title}</Title>
            </GradientStack>
          </TextStack>
        </GridWrapper>
      </ImageComponent>
    </StyledVideoRecommendationsItem>
  )
}

export default VideoRecommendationsItem
